import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, map, type Observable, of, tap } from 'rxjs';

import type { MicrophonePermission } from '../../models/microphone-permission.model';
import { MICROPHONE_PERMISSION_PROVIDER } from '../../tokens/voice-message.tokens';
import type { IMicrophoneAccessHandler } from './i-microphone-access.handler';

/**
 * Chi tiene il permesso del microfono per tutto il portale: la chat lo chiede quando si tocca il
 * microfono, le impostazioni lo mostrano e lo chiedono quando si accende la dettatura. Vive alla radice
 * per questo — sulle sole rotte della chat, le impostazioni non lo vedrebbero.
 */
@Injectable()
export class MicrophoneAccessHandler implements IMicrophoneAccessHandler {
  private readonly provider = inject(MICROPHONE_PERMISSION_PROVIDER);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _permission = signal<MicrophonePermission>('unknown');
  readonly permission = this._permission.asReadonly();

  constructor() {
    // Lo stato serve prima del primo tocco: leggerlo allora vorrebbe dire aspettare una domanda in piu'
    // fra il gesto e la richiesta, e portarla fuori dal gesto.
    this.refresh();
  }

  refresh(): void {
    this.provider
      .state()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((permission) => this._permission.set(permission));
  }

  requestAccess(): Observable<boolean> {
    if (this._permission() === 'denied') return of(false);

    return this.provider.request().pipe(
      map(() => true),
      tap(() => this._permission.set('granted')),
      catchError(() => {
        this.refresh();
        return of(false);
      }),
    );
  }
}
